/**
 * RisikoDeckungTab — the Police-Detail «Risiko & Deckung» tab (P2 Placement Slice 2, Ch 7.2).
 *
 *   branchencode null       → «Sparte festlegen» (opens the PolicyEditDialog Sparte select)
 *   branchencode family head→ «Sparte präzisieren» (pick a leaf code → PATCH igb2b_code)
 *   else                    → the sections, each behind its own Kachel visibility key:
 *       policen.risiko_deckung.ist          → IstAttributeSection (policy-level set + 91000 mirror)
 *       policen.risiko_deckung.objekte      → ObjektListe (per-object sets, add/remove)
 *       policen.risiko_deckung.soll         → SollGapSection (Soll-Deckung & Gap)
 *       policen.risiko_deckung.schadenverlauf → SchadenverlaufSection (Slice 6)
 *
 * Edit controls are gated on `canWrite` (policy_risk.write); the api is the hard line.
 */

import { useState } from 'react';
import { igb2bCodeLabel } from '@brokly/shared';
import type { PoliceRisikoObjekt } from '@brokly/shared';
import { Button } from '../../../../components/ui/Button';
import {
  usePoliceRisikoAttributes,
  usePoliceTargetCoverage,
  usePoliceRisikoPatch,
  usePoliceRiskObjectRemove,
  usePoliceTargetCoveragePut,
  usePolicyReclassify,
} from '../../hooks/usePoliceRisiko';
import { IstAttributeSection } from './IstAttributeSection';
import { ObjektListe } from './ObjektListe';
import { SollGapSection } from './SollGapSection';
import { SchadenverlaufSection } from './SchadenverlaufSection';

/** The empty policy-level fallback, shown before the first save. `total` reflects the branch
 *  catalog size so the counter reads "0/N" (not "0/0") from the very first render (m3). */
function emptyPolicyLevel(total: number): PoliceRisikoObjekt {
  return {
    id: '',
    insurance_object_ref: null,
    object_label: null,
    attributes: {},
    source: 'manuell',
    filled: 0,
    total,
    updated_at: new Date(0).toISOString(),
  };
}

export function RisikoDeckungTab({
  policyId,
  canWrite,
  isHidden,
  onSetSparte,
}: {
  policyId: string;
  canWrite: boolean;
  isHidden: (key: string) => boolean;
  /** Open the PolicyEditDialog to set the Sparte (when the policy is unclassified). */
  onSetSparte: () => void;
}) {
  const attrsQuery = usePoliceRisikoAttributes(policyId);
  const coverageQuery = usePoliceTargetCoverage(policyId);
  const patch = usePoliceRisikoPatch(policyId);
  const removeObject = usePoliceRiskObjectRemove(policyId);
  const coveragePut = usePoliceTargetCoveragePut(policyId);
  const reclassify = usePolicyReclassify(policyId);
  const [leaf, setLeaf] = useState('');

  if (attrsQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">Lade Risiko & Deckung…</p>;
  }
  if (attrsQuery.isError || !attrsQuery.data) {
    return (
      <p role="alert" className="text-sm text-destructive" data-testid="pr-error">
        Risiko & Deckung konnten nicht geladen werden.
      </p>
    );
  }

  const data = attrsQuery.data;

  // Unclassified policy → the broker must set the Sparte first.
  if (data.branchencode === null) {
    return (
      <div className="rounded-lg border bg-card p-6 text-center" data-testid="pr-sparte-festlegen">
        <h3 className="text-sm font-semibold text-foreground">Sparte festlegen</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Fuer diese Police ist noch keine IGB2B-Sparte hinterlegt. Legen Sie die Sparte fest, um
          Ist-Attribute und Soll-Deckung zu erfassen.
        </p>
        {canWrite && (
          <Button size="sm" className="mt-3" onClick={onSetSparte} data-testid="pr-sparte-festlegen-btn">
            Sparte festlegen
          </Button>
        )}
      </div>
    );
  }

  // Family-head classification → the broker precises it to a leaf code.
  if (data.is_family_head) {
    return (
      <div className="rounded-lg border bg-card p-6" data-testid="pr-sparte-praezisieren">
        <h3 className="text-sm font-semibold text-foreground">Sparte praezisieren</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Diese Police traegt die Oberbranche {igb2bCodeLabel(data.branchencode)}. Waehlen Sie die
          konkrete Sparte, um die branchenspezifischen Attribute zu erfassen.
        </p>
        {canWrite && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <select
              value={leaf}
              onChange={(e) => setLeaf(e.target.value)}
              aria-label="Sparte waehlen"
              className="rounded-md border bg-background px-2 py-1 text-sm"
              data-testid="pr-sparte-leaf-select"
            >
              <option value="">Sparte waehlen…</option>
              {data.leaf_codes.map((code) => (
                <option key={code} value={code}>
                  {igb2bCodeLabel(code)}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              disabled={leaf === '' || reclassify.isPending}
              onClick={() => reclassify.mutate(leaf)}
              data-testid="pr-sparte-leaf-save"
            >
              {reclassify.isPending ? 'Speichern…' : 'Uebernehmen'}
            </Button>
          </div>
        )}
      </div>
    );
  }

  const felder = data.felder;
  const policyLevel = data.objekte.find((o) => o.insurance_object_ref === null) ?? emptyPolicyLevel(felder.length);
  const objects = data.objekte.filter((o) => o.insurance_object_ref !== null);
  const isSaving = patch.isPending || removeObject.isPending;

  return (
    <div className="flex flex-col gap-5" data-testid="risiko-deckung-tab">
      {!isHidden('policen.risiko_deckung.ist') && (
        <IstAttributeSection
          felder={felder}
          objekt={policyLevel}
          canWrite={canWrite}
          isSaving={isSaving}
          onPatch={(body) => patch.mutate(body)}
          gesundheitspruefungStatus={data.gesundheitspruefung_status}
          gatedSuppressed={data.gated_fields_suppressed}
        />
      )}

      {!isHidden('policen.risiko_deckung.objekte') && (
        <ObjektListe
          felder={felder}
          objekte={objects}
          canWrite={canWrite}
          isSaving={isSaving}
          onPatch={(body) => patch.mutate(body)}
          onRemove={(objectId) => removeObject.mutate(objectId)}
        />
      )}

      {!isHidden('policen.risiko_deckung.soll') && coverageQuery.data && (
        <SollGapSection
          items={coverageQuery.data.items}
          canWrite={canWrite}
          onPut={(ruleKey, body) => coveragePut.mutate({ ruleKey, body })}
        />
      )}

      {!isHidden('policen.risiko_deckung.schadenverlauf') && (
        <SchadenverlaufSection policyId={policyId} canWrite={canWrite} />
      )}
    </div>
  );
}
